import { z } from "zod";

/**
 * Environment validation.
 *
 * Every process validates its slice of the environment at boot and exits on
 * failure. A production process must never start with a missing secret and
 * discover it later during a customer request.
 */

const nodeEnv = z.enum(["development", "test", "production"]).default("development");

const secret = (minLength: number) =>
	z
		.string()
		.min(minLength, `must be at least ${minLength} characters`)
		.refine((v) => !/^(changeme|placeholder|secret|test)$/i.test(v), {
			message: "must not be a placeholder value",
		});

const baseSchema = z.object({
	NODE_ENV: nodeEnv,
	LOG_LEVEL: z.enum(["debug", "info", "warn", "error"]).default("info"),
	APP_URL: z.string().url(),
	WEBMAIL_URL: z.string().url(),
	ADMIN_URL: z.string().url(),
	MARKETING_URL: z.string().url(),
	API_URL: z.string().url(),
});

const apiSchema = baseSchema.extend({
	PORT: z.coerce.number().int().positive().default(4000),
	DATABASE_URL: z.string().url(),
	REDIS_URL: z.string().url(),

	SESSION_SECRET: secret(32),
	/** Used for reversible encryption of temporary IMAP migration credentials. */
	APP_ENCRYPTION_KEY: secret(32),
	COOKIE_DOMAIN: z.string().optional(),

	MAIL_PLATFORM: z.enum(["stalwart", "fake"]).default("fake"),
	STALWART_BASE_URL: z.string().url().optional(),
	STALWART_API_KEY: z.string().optional(),
	STALWART_JMAP_URL: z.string().url().optional(),
	MAIL_HOST: z.string().default("mail.key.my"),
	MX_PRIMARY: z.string().default("mx1.key.my"),
	MX_SECONDARY: z.string().default("mx2.key.my"),
	SPF_INCLUDE: z.string().default("_spf.key.my"),
	/** DMARC aggregate reports (TXT at _dmarc). Set to dmarc@bafe.app to roll back after a rebrand. */
	DMARC_RUA_ADDRESS: z.string().email().default("dmarc@key.my"),

	/** Sites origin only. Do not reuse for Hub or Webmail. */
	SITES_CNAME_TARGET: z.string().default("sites-origin.key.my"),
	HUB_CNAME_TARGET: z.string().default("hub-origin.key.my"),
	WEBMAIL_CNAME_TARGET: z.string().default("webmail-origin.key.my"),
	SITE_IPV4: z.string().optional(),
	SITE_IPV6: z.string().optional(),
	/** Public static-site listener. Unset in local work that does not serve customer sites. */
	SITES_PUBLIC_PORT: z.coerce.number().int().positive().optional(),
	SITES_FILES_ROOT: z.string().optional(),
	TEAM_MEDIA_ROOT: z.string().optional(),
	/**
	 * Customer SQL server. Must not be the control-plane database.
	 * A missing value leaves database creation unavailable; it does not stop the API.
	 */
	HOSTING_DATABASE_URL: z.string().min(1).optional(),

	WEBMAIL_VAPID_PUBLIC_KEY: z.string().min(1).optional(),
	WEBMAIL_VAPID_PRIVATE_KEY: z.string().min(1).optional(),
	WEBMAIL_VAPID_SUBJECT: z.string().url().optional(),

	BILLING_PROVIDER: z.enum(["fake", "stripe"]).default("fake"),
	BILLING_SECRET: z.string().optional(),
	BILLING_WEBHOOK_SECRET: z.string().optional(),

	/** `fake` stores posts locally. `postiz` calls Postiz. `key` publishes Facebook Pages from Key. */
	SOCIAL_PLATFORM: z.enum(["fake", "postiz", "key"]).default("fake"),
	POSTIZ_URL: z.string().url().optional(),
	POSTIZ_API_KEY: z.string().optional(),
	FACEBOOK_APP_ID: z.string().min(1).optional(),
	FACEBOOK_APP_SECRET: z.string().min(1).optional(),
	FACEBOOK_REDIRECT_URL: z.string().url().optional(),
	/** Directory for Social photos. Defaults to data/social under the process working directory. */
	SOCIAL_MEDIA_DIR: z.string().min(1).optional(),

	ENGAGE_DRIVER: z.enum(["fake", "live"]).default("fake"),
	/**
	 * `fake` is local demo only. `connector` is the Key Engage sidecar
	 * (WhatsApp Linked Devices + Telegram MTProto). `mautrix` is an alias for
	 * `connector`. `live` is official Page APIs and must never send WhatsApp.
	 */
	ENGAGE_PLATFORM: z.enum(["fake", "live", "mautrix", "connector"]).optional(),
	ENGAGE_BRIDGE_URL: z.string().url().optional(),
	ENGAGE_BRIDGE_TOKEN: z.string().optional(),
	ENGAGE_CONNECTOR_ID: z.string().min(1).optional(),
	ENGAGE_CONNECTOR_SECRET: z.string().min(16).optional(),
	ENGAGE_WEBHOOK_SECRET: z.string().optional(),
	ENGAGE_OAUTH_REDIRECT_URL: z.string().url().optional(),
	ENGAGE_META_APP_ID: z.string().optional(),
	ENGAGE_META_APP_SECRET: z.string().optional(),
	ENGAGE_X_CLIENT_ID: z.string().optional(),
	ENGAGE_X_CLIENT_SECRET: z.string().optional(),
	ENGAGE_TIKTOK_CLIENT_KEY: z.string().optional(),
	ENGAGE_TIKTOK_CLIENT_SECRET: z.string().optional(),
	ENGAGE_LINKEDIN_CLIENT_ID: z.string().optional(),
	ENGAGE_LINKEDIN_CLIENT_SECRET: z.string().optional(),
	ENGAGE_TELEGRAM_BOT_ID: z.string().optional(),
	ENGAGE_TELEGRAM_BOT_TOKEN: z.string().optional(),
	ENGAGE_TELEGRAM_API_ID: z.string().optional(),
	ENGAGE_TELEGRAM_API_HASH: z.string().optional(),
	ENGAGE_ALLOW_DEMO: z.enum(["true", "false"]).optional(),

	CLOUDFLARE_CLIENT_ID: z.string().optional(),
	CLOUDFLARE_CLIENT_SECRET: z.string().optional(),
	/** Scoped Cloudflare API token with DNS edit on customer zones (spec §38). */
	CLOUDFLARE_DNS_TOKEN: z.string().optional(),

	NOTIFICATION_TRANSPORT: z.enum(["smtp", "console"]).default("console"),
	NOTIFICATION_SMTP_URL: z.string().optional(),
	NOTIFICATION_FROM: z.string().default("Key <noreply@key.my>"),

	/**
	 * Comma-separated Bafe user emails allowed into /dash. When set, only
	 * these accounts are operators — development no longer treats every
	 * signed-in user as staff.
	 */
	OPERATOR_EMAILS: z.string().optional(),

	OBJECT_STORAGE_ENDPOINT: z.string().url().optional(),
	OBJECT_STORAGE_BUCKET: z.string().optional(),
	OBJECT_STORAGE_ACCESS_KEY: z.string().optional(),
	OBJECT_STORAGE_SECRET_KEY: z.string().optional(),
});

/**
 * Production must not silently fall back to development stand-ins. The fake
 * mail platform and fake billing provider exist for local work and tests only.
 */
const apiSchemaChecked = apiSchema
	.refine((env) => env.NODE_ENV !== "production" || env.MAIL_PLATFORM === "stalwart", {
		message: "MAIL_PLATFORM must be 'stalwart' in production",
		path: ["MAIL_PLATFORM"],
	})
	.refine((env) => env.MAIL_PLATFORM !== "stalwart" || Boolean(env.STALWART_BASE_URL && env.STALWART_API_KEY), {
		message: "STALWART_BASE_URL and STALWART_API_KEY are required when MAIL_PLATFORM=stalwart",
		path: ["STALWART_BASE_URL"],
	})
	.refine((env) => env.NODE_ENV !== "production" || env.BILLING_PROVIDER !== "fake", {
		message: "BILLING_PROVIDER must not be 'fake' in production",
		path: ["BILLING_PROVIDER"],
	})
	.refine((env) => env.BILLING_PROVIDER === "fake" || Boolean(env.BILLING_WEBHOOK_SECRET && env.BILLING_SECRET), {
		message: "BILLING_SECRET and BILLING_WEBHOOK_SECRET are required for a real billing provider",
		path: ["BILLING_SECRET"],
	})
	.refine((env) => env.SOCIAL_PLATFORM !== "postiz" || Boolean(env.POSTIZ_URL && env.POSTIZ_API_KEY), {
		message: "POSTIZ_URL and POSTIZ_API_KEY are required when SOCIAL_PLATFORM=postiz",
		path: ["POSTIZ_URL"],
	})
	.refine((env) => env.SOCIAL_PLATFORM !== "key" || Boolean(env.FACEBOOK_APP_ID && env.FACEBOOK_APP_SECRET), {
		message: "FACEBOOK_APP_ID and FACEBOOK_APP_SECRET are required when SOCIAL_PLATFORM=key",
		path: ["FACEBOOK_APP_ID"],
	})
	.refine((env) => env.NODE_ENV !== "production" || (env.ENGAGE_PLATFORM ?? "connector") !== "fake", {
		message: "ENGAGE_PLATFORM must not be 'fake' in production",
		path: ["ENGAGE_PLATFORM"],
	})
	.refine(
		(env) => {
			const driver = env.ENGAGE_PLATFORM ?? (env.ENGAGE_DRIVER === "live" ? "live" : "fake");
			if (driver !== "connector" && driver !== "mautrix") return true;
			return Boolean(env.ENGAGE_BRIDGE_URL && env.ENGAGE_BRIDGE_TOKEN && env.ENGAGE_CONNECTOR_SECRET);
		},
		{
			message: "ENGAGE_BRIDGE_URL, ENGAGE_BRIDGE_TOKEN and ENGAGE_CONNECTOR_SECRET are required for the Engage connector",
			path: ["ENGAGE_BRIDGE_URL"],
		},
	)
	.refine((env) => env.NODE_ENV !== "production" || Boolean(env.ENGAGE_CONNECTOR_SECRET || env.ENGAGE_WEBHOOK_SECRET), {
		message: "ENGAGE_CONNECTOR_SECRET is required in production so inbound Engage events can be verified",
		path: ["ENGAGE_CONNECTOR_SECRET"],
	});

const workerSchema = apiSchema.extend({
	WORKER_CONCURRENCY: z.coerce.number().int().positive().default(4),
	MIGRATION_CONCURRENCY: z.coerce.number().int().positive().default(2),
	DNS_CHECK_INTERVAL_SECONDS: z.coerce.number().int().positive().default(300),
});

export type ApiEnv = z.infer<typeof apiSchema>;
export type WorkerEnv = z.infer<typeof workerSchema>;

export class EnvValidationError extends Error {
	constructor(readonly issues: z.ZodIssue[]) {
		const lines = issues.map((i) => `  ${i.path.join(".") || "(root)"}: ${i.message}`);
		super(`Invalid environment configuration:\n${lines.join("\n")}`);
		this.name = "EnvValidationError";
	}
}

/**
 * Generic over the schema, not its output type. Several fields carry a
 * `.default()`, so the schema's input and output types differ and a
 * `z.ZodType<T>` parameter would unify T against the input.
 */
function parseOrThrow<S extends z.ZodTypeAny>(schema: S, source: NodeJS.ProcessEnv): z.infer<S> {
	const result = schema.safeParse(source);
	if (!result.success) throw new EnvValidationError(result.error.issues);
	return result.data;
}

export function loadApiEnv(source: NodeJS.ProcessEnv = process.env): ApiEnv {
	return parseOrThrow(apiSchemaChecked, source);
}

export function loadWorkerEnv(source: NodeJS.ProcessEnv = process.env): WorkerEnv {
	return parseOrThrow(workerSchema, source);
}
